import { useState, useEffect } from "react";
import { Form, FormDate, FormActions, FormInput } from "../Controls/Form";
import PackageItemListItem from "../ListItems/PackageItemListItem";
import { Link } from "react-router-dom";
import Helpers from "../Controls/helpers";
import UserData from "../Controls/UserData";
import JobPostingSelectionListItem from "../ListItems/JobPostingSelectionListItem";
import Help from "../Controls/Help";
import API from "@src/API.js";
import { MdAddBox } from "react-icons/md";
import { toast } from "react-toastify";

function emptyItemsFromLookups(lookups) {
  const types = lookups?.PackageItemTypes || lookups?.packageItemTypes || [];
  if (!Array.isArray(types)) return [];
  return types.map((pit) => {
    const key = pit.Key ?? pit.key ?? pit.ID ?? pit.id;
    const value = pit.Value ?? pit.value ?? pit;
    return {
      ID: 0,
      DataType: value?.DataType ?? value?.dataType,
      TypeDescription: value?.Description ?? value?.description ?? "",
      ItemTypeName: value?.Text ?? value?.text ?? value?.Name ?? value?.name ?? "",
      Description: "",
      Default: value?.Default ?? value?.default,
      Text: value?.Text ?? value?.text ?? "",
      ItemTypeID: key,
      Enabled: false,
      Value: "",
    };
  });
}

/**
 * Package form body. Prefer PackageEditModal for UI entry points.
 */
export default function PackageEdit({ packageId = 0, employerId = 0, onClose }) {
  const [lookups] = useState(UserData.Lookups() || {});
  const [ID, setID] = useState(Number.parseInt(packageId, 10) || 0);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("info");
  const empId = Number.parseInt(employerId, 10) || 0;
  const [pkg, updatePackage] = useState({
    Name: "",
    EmployerID: empId,
    Items: emptyItemsFromLookups(lookups),
    Jobs: [],
  });

  useEffect(() => {
    setID(Number.parseInt(packageId, 10) || 0);
  }, [packageId]);

  useEffect(() => {
    if (!ID) {
      updatePackage((prev) => ({
        ...prev,
        EmployerID: empId || prev.EmployerID,
        Items: prev.Items?.length ? prev.Items : emptyItemsFromLookups(lookups),
      }));
      return;
    }
    let cancelled = false;
    (async () => {
      const resp = await API.getAsync("/Package?ID=" + ID);
      if (cancelled) return;
      if (resp?.ok && resp.jsonData) {
        const data = resp.jsonData;
        if (!data.PayCurrencyID && !data.payCurrencyID) data.PayCurrencyID = 1;
        if (!data.EmploymentTypeID && !data.employmentTypeID) data.EmploymentTypeID = 10;
        if (!data.PayIntervalID && !data.PayInterval && !data.payIntervalID) data.PayIntervalID = 1;
        updatePackage({
          ...data,
          // WriteAsJsonAsync camelCases properties; Form fields use PascalCase.
          Name: data.Name || data.name || "",
          EffectiveDate: data.EffectiveDate || data.effectiveDate || null,
          KillDate: data.KillDate || data.killDate || null,
          EmployerID: data.EmployerID || data.employerID || empId || 0,
          Items: data.Items || data.items || [],
          Jobs: data.Jobs || data.jobs || [],
        });
      } else {
        toast.error("Could not load package");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ID, empId, lookups]);

  function close() {
    onClose?.();
  }

  async function onDelete() {
    if (!ID) {
      close();
      return;
    }
    const resp = await UserData.DeleteAsync("Package", ID);
    if (resp?.result) {
      toast.success("Package deleted");
      close();
    } else {
      toast.error(resp?.error || "Delete failed");
    }
  }

  async function onSave() {
    if (busy) return;
    setBusy(true);
    try {
      const data = {
        ...pkg,
        ID: ID || pkg.ID || pkg.id || 0,
        Name: pkg.Name || pkg.name || "",
        EmployerID: pkg.EmployerID || pkg.employerID || empId || 0,
        Items: (pkg.Items || pkg.items || []).filter((item) => item.Enabled ?? item.enabled),
      };
      if (!data.EmployerID) {
        toast.error("Employer is required to save a package");
        return;
      }
      const resp = await API.postAsync("/Package", data);
      if (resp?.ok) {
        const newId =
          typeof resp.jsonData === "number"
            ? resp.jsonData
            : resp.jsonData?.ID || resp.jsonData?.id || ID;
        if (newId && !ID) setID(newId);
        toast.success("Package saved");
        close();
      } else {
        toast.error(resp?.jsonData?.message || "Save failed");
      }
    } finally {
      setBusy(false);
    }
  }

  function onToggleEnabled(itemTypeId) {
    const items = [...(pkg.Items || [])];
    const item = Helpers.findWithAttr(items, "ItemTypeID", itemTypeId);
    if (!item) return;
    item.Enabled = !item.Enabled;
    updatePackage({ ...pkg, Items: items });
  }

  function onChange(newValue, description, itemTypeId) {
    const items = [...(pkg.Items || [])];
    const item = Helpers.findWithAttr(items, "ItemTypeID", itemTypeId);
    if (!item) return;
    item.Description = description;
    item.Value = newValue;
    updatePackage({ ...pkg, Items: items });
  }

  function onToggleJob(id) {
    const jobs = [...(pkg.Jobs || [])];
    const job = jobs.find((j) => (j.ID || j.id) === id);
    if (job) job.Selected = !job.Selected;
    updatePackage({ ...pkg, Jobs: jobs });
  }

  const items = pkg.Items || [];
  const jobs = pkg.Jobs || [];
  const enabledPackageItemCount = items.filter((i) => i.Enabled).length;
  const selectedJobCount = jobs.filter((j) => j.Selected ?? j.selected).length;

  return (
    <div className="cm-modal-embedded packageEdit">
      <Form className="cm-modal-form" object={pkg} onUpdate={updatePackage}>
        <div className="cm-modal-tabs" role="tablist" aria-label="Package sections">
          <button
            type="button"
            role="tab"
            id="package-tab-info"
            aria-selected={tab === "info"}
            aria-controls="package-panel-info"
            className={tab === "info" ? "is-active" : undefined}
            data-testid="package-tab-info"
            onClick={() => setTab("info")}
          >
            Info
          </button>
          <button
            type="button"
            role="tab"
            id="package-tab-items"
            aria-selected={tab === "items"}
            aria-controls="package-panel-items"
            className={tab === "items" ? "is-active" : undefined}
            data-testid="package-tab-items"
            onClick={() => setTab("items")}
          >
            Items{enabledPackageItemCount > 0 ? ` (${enabledPackageItemCount})` : ""}
          </button>
          <button
            type="button"
            role="tab"
            id="package-tab-packages"
            aria-selected={tab === "packages"}
            aria-controls="package-panel-packages"
            className={tab === "packages" ? "is-active" : undefined}
            data-testid="package-tab-packages"
            onClick={() => setTab("packages")}
          >
            Packages{selectedJobCount > 0 ? ` (${selectedJobCount})` : ""}
          </button>
        </div>

        <div className="cm-modal-tab-panels">
          <div
            id="package-panel-info"
            role="tabpanel"
            aria-labelledby="package-tab-info"
            aria-hidden={tab !== "info"}
            className={`cm-modal-tab-panel${tab === "info" ? " is-active" : ""}`}
            {...(tab !== "info" ? { inert: "" } : {})}
          >
            <Help id="2122" />
            <FormInput field="Name" label="Name" />
            <FormDate label="Effective Date" field="EffectiveDate" />
            <FormDate label="Expiration Date" field="KillDate" nullable="true" />
          </div>

          <div
            id="package-panel-items"
            role="tabpanel"
            aria-labelledby="package-tab-items"
            aria-hidden={tab !== "items"}
            className={`cm-modal-tab-panel${tab === "items" ? " is-active" : ""}`}
            {...(tab !== "items" ? { inert: "" } : {})}
          >
            <div className="listTitle package-modal-list-title">
              <span>
                Package items ({enabledPackageItemCount} of {items.length})
                <Help id="2127" />
              </span>
              <div className="listTitleIcons">
                <Link
                  to="/packageItemEdit"
                  state={{
                    PackageID: ID,
                    EmployerID: pkg.EmployerID || empId,
                  }}
                  title="Add package item type"
                >
                  <MdAddBox />
                </Link>
              </div>
            </div>
            <p className="fieldHint">
              Select the items that apply to this package and describe each benefit for candidates.
            </p>
            <ul className="package-modal-item-list">
              {items.map((item) => (
                <li key={item.ItemTypeID || item.ID}>
                  <PackageItemListItem
                    value={item}
                    onChange={onChange}
                    onToggleEnabled={onToggleEnabled}
                  />
                </li>
              ))}
            </ul>
          </div>

          <div
            id="package-panel-packages"
            role="tabpanel"
            aria-labelledby="package-tab-packages"
            aria-hidden={tab !== "packages"}
            className={`cm-modal-tab-panel${tab === "packages" ? " is-active" : ""}`}
            {...(tab !== "packages" ? { inert: "" } : {})}
          >
            <p className="fieldHint">Job postings that use this package.</p>
            {jobs.length === 0 ? (
              <p className="fieldHint">No job postings linked yet.</p>
            ) : (
              <ul className="package-modal-job-list">
                {jobs.map((j) => {
                  const jid = j.ID || j.id;
                  return (
                    <li key={jid}>
                      <JobPostingSelectionListItem
                        id={jid}
                        name={j.Name || j.name}
                        selected={!!(j.Selected ?? j.selected)}
                        onChange={onToggleJob}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <FormActions
          doneLabel={busy ? "Saving…" : "Save"}
          onCancelAsync={async () => {
            close();
          }}
          onDeleteAsync={
            ID
              ? async () => {
                  await onDelete();
                }
              : undefined
          }
          onSaveAsync={async () => {
            await onSave();
          }}
        />
      </Form>
    </div>
  );
}
